import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import Header from './Header';

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const isLandingPage = location.pathname === '/';
  const isAuthPage = location.pathname === '/auth';
  const isLegalPage = location.pathname.startsWith('/legal') || 
                      location.pathname.startsWith('/cgv') || 
                      location.pathname.startsWith('/privacy') || 
                      location.pathname.startsWith('/cookies');
  const requiresGate = !isLandingPage && !isAuthPage && !isLegalPage;
  const { user, markOnboardingVideoSeen } = useAuth();
  const legacyUserKey = user ? `onboarding_video_watched_user_${user.id}` : null;
  const hasSeenOnboardingVideo = user?.has_seen_onboarding_video ?? false;

  const [showVideoGate, setShowVideoGate] = useState(false);
  const [videoKey, setVideoKey] = useState(0); // reset video when reopening
  const [isPersistingChoice, setIsPersistingChoice] = useState(false);

  useEffect(() => {
    if (!requiresGate || !user) {
      setShowVideoGate(false);
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }
    if (hasSeenOnboardingVideo) {
      setShowVideoGate(false);
      return;
    }

    const legacyWatched = legacyUserKey 
      ? window.localStorage.getItem(legacyUserKey) === 'true'
      : false;

    if (legacyWatched) {
      markOnboardingVideoSeen().catch(err => {
        console.error('Erreur synchronisation du statut vidéo onboarding:', err);
      });
      setShowVideoGate(false);
    } else {
      setShowVideoGate(true);
      setVideoKey(prev => prev + 1);
    }
  }, [requiresGate, user, legacyUserKey, hasSeenOnboardingVideo, markOnboardingVideoSeen, location.pathname]);

  const handleVideoCompleted = useCallback(async () => {
    if (isPersistingChoice) {
      return;
    }
    setIsPersistingChoice(true);
    try {
      await markOnboardingVideoSeen();
      if (legacyUserKey && typeof window !== 'undefined') {
        window.localStorage.setItem(legacyUserKey, 'true');
      }
      setShowVideoGate(false);
    } catch (error) {
      console.error('Erreur lors de la confirmation de visionnage:', error);
    } finally {
      setIsPersistingChoice(false);
    }
  }, [legacyUserKey, markOnboardingVideoSeen, isPersistingChoice]);

  return (
    <div className="min-h-screen bg-gray-50">
      {!isLandingPage && !isAuthPage && !isLegalPage && <Header />}
      <main className={isLandingPage || isAuthPage || isLegalPage ? '' : 'p-6'}>
        {children}
      </main>
      {requiresGate && user && showVideoGate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl p-6">
            <h2 className="text-2xl font-semibold text-gray-900 text-center">Bienvenue 👋</h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Regarde cette courte vidéo avant de commencer à utiliser Asinder.
            </p>
            <div className="mt-6 rounded-xl overflow-hidden border border-gray-200 shadow-lg">
              <video
                key={videoKey}
                className="w-full"
                controls
                playsInline
                controlsList="nodownload"
                onEnded={handleVideoCompleted}
              >
                <source src="/videos/filtrage-intelligent-demo-compressed.mp4" type="video/mp4" />
                Votre navigateur ne supporte pas la lecture de vidéos.
              </video>
            </div>
            <p className="mt-4 text-xs text-center text-gray-500">
              La vidéo doit être visionnée entièrement pour continuer.
            </p>
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={handleVideoCompleted}
                disabled={isPersistingChoice}
                className="text-xs text-blue-600 hover:text-blue-700 underline disabled:text-gray-400"
              >
                J&apos;ai déjà visionné cette vidéo
              </button>
            </div>
            <div className="mt-2 text-center">
              <button
                type="button"
                onClick={handleVideoCompleted}
                disabled={isPersistingChoice}
                className="text-xs text-gray-600 hover:text-gray-800 underline disabled:text-gray-400"
              >
                Ne plus afficher cette vidéo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
